// Wrapper reactivo mínimo sobre supabase.auth para este panel.
// No se sube a @plataforma/supabase-client porque el patrón de suscripción
// (un solo listener global + notificación a quien se suscriba) es una
// decisión de estado de UI, no del cliente de datos — supabase-client se
// mantiene como lo que es: solo creación del cliente + lectura de claims
// (getClaims), sin opinión sobre cómo cada app reacciona a los cambios.

import { getClaims, supabaseClient } from '@plataforma/supabase-client';

import { obtenerEmpresaActual } from './api/empresa-perfil.js';
import { navegarA } from './enrutador.js';

/** @type {Set<(estado: EstadoSesion) => void>} */
const subscriptores = new Set();

/**
 * @typedef {object} EstadoSesion
 * @property {import('@supabase/supabase-js').Session | null} sesion
 * @property {ReturnType<typeof getClaims>} claims
 * @property {boolean} cargando
 * @property {import('./api/empresa-perfil.js').EmpresaConPlan | null} empresa
 */

/** @type {EstadoSesion} */
let estadoActual = {
  sesion: null,
  claims: { empresa_id: null, rol: null },
  cargando: true,
  empresa: null,
};

function notificar() {
  for (const fn of subscriptores) fn(estadoActual);
}

async function cargarEmpresa() {
  if (!estadoActual.sesion?.user) {
    estadoActual = { ...estadoActual, empresa: null };
    notificar();
    return;
  }

  estadoActual = { ...estadoActual, cargando: true };
  notificar();

  const { data } = await obtenerEmpresaActual();

  estadoActual = { ...estadoActual, empresa: data ?? null, cargando: false };
  notificar();
}

/**
 * Inicializa la escucha de cambios de sesión. Debe llamarse una sola vez al
 * arrancar la app (main.js), antes de montar el router.
 */
export async function inicializarSesion() {
  const {
    data: { session },
  } = await supabaseClient().auth.getSession();
  estadoActual = { sesion: session, claims: getClaims(session), cargando: false, empresa: null };
  notificar();

  await cargarEmpresa();

  supabaseClient().auth.onAuthStateChange(async (evento, session) => {
    const eventosQueCambianEstado = new Set([
      'SIGNED_IN',
      'SIGNED_OUT',
      'USER_UPDATED',
      'PASSWORD_RECOVERY',
    ]);

    if (eventosQueCambianEstado.has(evento)) {
      estadoActual = {
        sesion: session,
        claims: getClaims(session),
        cargando: false,
        empresa: null,
      };
      notificar();

      if (!session) return;
      await cargarEmpresa();
    } else if (evento === 'TOKEN_REFRESHED' && session) {
      // Actualizar tokens internos SIN disparar re-render: el usuario sigue
      // autenticado y sus claims no cambian. Evita perder selección de archivo
      // en móviles cuando Supabase refresca el token al volver de la galería.
      estadoActual = {
        ...estadoActual,
        sesion: session,
        claims: getClaims(session),
      };
    }

    if (evento === 'PASSWORD_RECOVERY') {
      navegarA('/cambiar-contrasena');
      return;
    }
  });
}

/** @returns {EstadoSesion} */
export function obtenerEstadoSesion() {
  return estadoActual;
}

/**
 * @param {(estado: EstadoSesion) => void} fn
 * @returns {() => void} función para cancelar la suscripción
 */
export function suscribirseASesion(fn) {
  subscriptores.add(fn);
  return () => subscriptores.delete(fn);
}

/**
 * Suscripción reactiva solo a cambios de empresa.
 * @param {(empresa: EstadoSesion['empresa']) => void} fn
 * @returns {() => void}
 */
export function suscribirseAEmpresa(fn) {
  const wrapper = (estado) => fn(estado.empresa);
  subscriptores.add(wrapper);
  return () => subscriptores.delete(wrapper);
}

/**
 * Fuerza la recarga de la empresa actual desde el backend.
 * Útil después de operaciones que puedan modificar el plan/features.
 */
export async function recargarEmpresa() {
  await cargarEmpresa();
}

/**
 * @param {string} email
 * @param {string} password
 */
export async function iniciarSesion(email, password) {
  return supabaseClient().auth.signInWithPassword({ email, password });
}

export async function cerrarSesion() {
  estadoActual = {
    sesion: null,
    claims: { empresa_id: null, rol: null },
    cargando: false,
    empresa: null,
  };
  notificar();
  await supabaseClient().auth.signOut();

  navegarA('/login');
}

/**
 * Cambia la contraseña del usuario que YA tiene una sesión activa —
 * sirve tanto para un cambio voluntario (admin_empresa/editor_empresa
 * logueado normalmente) como para completar una recuperación (sesión
 * temporal creada por el evento PASSWORD_RECOVERY de arriba). Supabase
 * no exige la contraseña anterior para esta llamada porque ya valida la
 * identidad a través de la sesión misma.
 *
 * @param {string} nuevaContrasena
 */
export async function actualizarContrasena(nuevaContrasena) {
  return supabaseClient().auth.updateUser({ password: nuevaContrasena });
}

/**
 * Envía el correo de "olvidé mi contraseña". Supabase responde con éxito
 * exista o no una cuenta con ese correo (por diseño, para no filtrar qué
 * correos están registrados). El llamador debe mostrar el mismo mensaje
 * en ambos casos.
 *
 * @param {string} email
 */
export async function solicitarRecuperacion(email) {
  return supabaseClient().auth.resetPasswordForEmail(email, {
    redirectTo: window.location.origin,
  });
}
